import { transformOnlineTransaction } from '@core/mappers/transaction/transformOnlineTransaction';
import { OnlineTransactionDto } from '@core/mappers/transaction/dtos/transactionDtos';
import {
    OnlineTransactionProvider,
    OnlineTransaction,
    OnlineDeposit,
    OnlineWithdrawal,
} from '@core/mappers/transaction/types';
import User from '@core/models/user/User';
import {
    TransactionType,
    AnalyticsEvent,
    PaymentStatus,
} from '@superbet-group/web.types';
import { firstTimeDeposit } from '@modules/userProfile/paymentGateway/enums';
import { TransactionMethod } from '@core/services/antiFraud/types';

type DepositPayload<EventT, EventLabelT> = {
    transactionId: string;
    transactionProvider: OnlineTransactionProvider;
    userId: User['userId'];
    depositStatus: PaymentStatus.Success;
    depositMethod: string;
    depositAmount: string;
    balance: number;
    event: EventT;
    eventLabel: EventLabelT;
    email: string;
};

type WithdrawalPayload = {
    transactionId: string;
    transactionProvider: OnlineTransactionProvider;
    withdrawalAmount: string;
};

type DepositPayloadStandard = DepositPayload<typeof AnalyticsEvent.DepositCompleteSB, 'Subsequent'>;
type DepositPayloadFtd = DepositPayload<typeof AnalyticsEvent.DepositCompleteFD, 'FTD'> & {
    eventLabel: 'FTD';
};

type EventPayloadError = {
    eventLabel: PaymentStatus.Failed | PaymentStatus.Cancel;
    eventMessage: string;
    transactionId?: string;
};

type TransactionEvent<EventT, PayloadT> = {
    event: EventT;
    payload: PayloadT;
    transactionDto?: OnlineTransactionDto; // This will be removed when feature flag is removed as it's a workaround to have a clean flow
};

type DepositSuccessPayload = TransactionEvent<typeof AnalyticsEvent.Deposit, DepositPayloadStandard | DepositPayloadFtd>;
type DepositFailurePayload = TransactionEvent<typeof AnalyticsEvent.DepositError, EventPayloadError>;

export type WithdrawalSuccessPayload = TransactionEvent<typeof AnalyticsEvent.WithdrawComplete, WithdrawalPayload>;
type WithdrawalFailurePayload = TransactionEvent<typeof AnalyticsEvent.WithdrawError, EventPayloadError>;

type OnlineTransactionPayload =
    | DepositFailurePayload
    | DepositSuccessPayload
    | WithdrawalFailurePayload
    | WithdrawalSuccessPayload;

type ProcessOnlineTransactionDepositArgs = {
    user: User;
    didUserDepositBefore: boolean;
};

export function processOnlineTransactionResponse(
    transactionDto: OnlineTransactionDto,
    depositArgs?: ProcessOnlineTransactionDepositArgs,
): OnlineTransactionPayload | undefined {
    const transaction = transformOnlineTransaction(transactionDto);

    if (isTransactionFailedOrCanceled(transaction)) {
        if (transaction.kind === TransactionType.Deposit) {
            return getFailedDepositPayload(transaction);
        } else {
            return getFailedWithdrawalPayload(transaction);
        }
    } else if (transaction.shouldBeReported) {
        if (transaction.kind === TransactionType.Withdrawal) {
            return getSuccessWithdrawalPayload(transaction);
        } else if (depositArgs) {
            return getSuccessDepositPayload(transaction, depositArgs.user, depositArgs.didUserDepositBefore);
        }
    }
}

export function getSuccessDepositPayload(
    transaction: OnlineDeposit,
    user: User,
    didUserDepositBefore: boolean,
) {
    const resultEvent: DepositSuccessPayload = {
        event: AnalyticsEvent.Deposit,
        payload: {
            depositStatus: PaymentStatus.Success,
            userId: user.userId,
            depositAmount: transaction.amount,
            event: AnalyticsEvent.DepositCompleteSB,
            eventLabel: 'Subsequent',
            email: user.email,
            balance: user.balance?.realMoney?.total ?? 0,
            transactionId: transaction.id,
            depositMethod: getDepositMethod(transaction),
            transactionProvider: transaction.provider,
        },
        transactionDto: transaction.transactionDto,
    };

    if (!didUserDepositBefore) {
        resultEvent.payload = {
            ...resultEvent.payload,
            ...{
                event: AnalyticsEvent.DepositCompleteFD,
                eventLabel: firstTimeDeposit,
            },
        };
    }

    return resultEvent;
}

export function getSuccessWithdrawalPayload(transaction: OnlineWithdrawal): WithdrawalSuccessPayload {
    return {
        event: AnalyticsEvent.WithdrawComplete,
        payload: {
            transactionId: transaction.id,
            transactionProvider: transaction.provider,
            withdrawalAmount: transaction.amount,
        },
    };
}

export function getFailedDepositPayload(transaction: OnlineDeposit): DepositFailurePayload {
    let eventMessage: string | undefined;
    if ('messageForAnalytics' in transaction.transactionDto) {
        eventMessage = transaction.transactionDto.messageForAnalytics;
    } else if ('reason' in transaction.transactionDto) {
        eventMessage = transaction.transactionDto.reason;
    }
    return {
        event: AnalyticsEvent.DepositError,
        payload: {
            eventLabel: transaction.status as PaymentStatus.Cancel | PaymentStatus.Failed,
            eventMessage: eventMessage ?? '',
            transactionId: transaction.id,
        },
        transactionDto: transaction.transactionDto,
    };
}

export function getFailedWithdrawalPayload(transaction: OnlineWithdrawal) : WithdrawalFailurePayload {
    return {
        event: AnalyticsEvent.WithdrawError,
        payload: {
            eventLabel: transaction.status as PaymentStatus.Cancel | PaymentStatus.Failed,
            eventMessage: 'failed',
            transactionId: transaction.id,
        },
    };
}

export function isTransactionFailedOrCanceled(transaction: OnlineTransaction): boolean {
    return transaction.status === PaymentStatus.Failed || transaction.status === PaymentStatus.Cancel;
}

function getDepositMethod(transaction: OnlineDeposit): string {
    if (transaction.method) {
        return transaction.method;
    }

    if (transaction.provider === TransactionMethod.PaySafe) {
        return 'apmgw_PaySafeCard';
    }

    return transaction.method;
}
